import { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '../atoms';
import { isIOS, downloadDocument } from '../lib/downloadDocument';
import { useToast } from './Toast';

/**
 * DocumentViewer — shared inline document preview (doc-viewer).
 *
 * The single fix for "documents only ever force-download, you can never just
 * VIEW them". Given a document + the download endpoint base for its context, it
 * fetches the bytes SAME-ORIGIN (credentials:'same-origin') as a Blob, mints an
 * object URL, and renders inline by content_type:
 *   - application/pdf → <iframe> (the CSP frameSrc allowlists blob:)
 *   - image/*         → <img>
 *   - text/* | csv    → fetched text in a scrollable <pre>
 *   - anything else   → a graceful "Preview not available — download instead"
 *
 * Actions: Download (attachment, the endpoint default), Open in new tab
 * (?disposition=inline), and — in modal mode — Close. The object URL is REVOKED
 * on close/unmount. Loading + error states are shown; a 410 reads as
 * "This document has no stored file".
 *
 * Two modes, one component:
 *   - onClose provided → MODAL overlay (backdrop + Close button + Esc-to-close).
 *     Used by the client portal.
 *   - onClose omitted  → EMBEDDED inline preview (no overlay). Used by the staff
 *     Document detail page.
 *
 * downloadBase is the FULL path to this document's download endpoint, e.g.
 * `/api/documents/:id/download` (staff) or
 * `/api/client-portal/documents/:id/download` (portal). The portal viewer must
 * ONLY ever be handed the portal base — never the staff endpoint.
 */

export interface DocumentViewerProps {
  id: string;
  name: string;
  filename: string;
  content_type: string | null;
  /** Full download endpoint path for this document (no query string). */
  downloadBase: string;
  /** When provided, render as a modal overlay with Close + Esc. */
  onClose?: () => void;
}

type Kind = 'pdf' | 'image' | 'text' | 'other';

function kindFor(contentType: string | null): Kind {
  const ct = (contentType || '').toLowerCase();
  if (ct.startsWith('application/pdf')) return 'pdf';
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('text/') || ct === 'text/csv' || ct.endsWith('/csv')) return 'text';
  return 'other';
}

export function DocumentViewer({ id, name, filename, content_type, downloadBase, onClose }: DocumentViewerProps) {
  const { toast } = useToast();
  const kind = kindFor(content_type);
  const isModal = typeof onClose === 'function';

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textBody, setTextBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(kind !== 'other');
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const inlineUrl = `${downloadBase}?disposition=inline`;

  // Esc closes (modal mode only).
  useEffect(() => {
    if (!isModal) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose!(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isModal, onClose]);

  // Fetch bytes → object URL (or text). Skipped for the 'other' fallback (no
  // preview to render). The object URL is revoked on unmount / dep change.
  useEffect(() => {
    if (kind === 'other') { setLoading(false); return; }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetch(inlineUrl, { credentials: 'same-origin', signal: controller.signal });
        if (!res.ok) {
          throw new Error(res.status === 410 ? 'This document has no stored file' : `Unable to load document (HTTP ${res.status})`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        if (kind === 'text') {
          const t = await blob.text();
          if (cancelled) return;
          setTextBody(t);
        } else {
          const url = URL.createObjectURL(blob);
          urlRef.current = url;
          setObjectUrl(url);
        }
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Unable to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [inlineUrl, kind]);

  const preview = (() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--sp-2)', minHeight: 240, color: 'var(--color-muted)' }}>
          <Spinner size="sm" /> Loading preview…
        </div>
      );
    }
    if (error) {
      return (
        <div style={{
          padding: 'var(--sp-5)', minHeight: 160, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)',
        }}>
          <p style={{ margin: 0, fontWeight: 'var(--fw-semibold)' }}>{error}</p>
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)' }}>You can still download the file below.</p>
        </div>
      );
    }
    if (kind === 'pdf' && objectUrl) {
      // F10 — iOS Safari renders a PDF inside an <iframe> unreliably (blank /
      // first-page-only / scroll-locked). On iOS, offer a tap-to-open panel that
      // hands the PDF to the native viewer in a new tab instead. Desktop + Android
      // keep the inline iframe unchanged.
      if (isIOS()) {
        return (
          <div style={{
            padding: 'var(--sp-6)', minHeight: 200, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 'var(--sp-3)', textAlign: 'center',
            background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)',
          }}>
            <p style={{ margin: 0, color: 'var(--color-ink-2)', fontSize: 'var(--fs-md)' }}>
              Tap to open <strong>{name || filename}</strong> in your PDF viewer.
            </p>
            <Button variant="primary" onClick={() => window.open(inlineUrl, '_blank', 'noopener,noreferrer')}>
              Open PDF
            </Button>
          </div>
        );
      }
      return (
        <iframe
          title={`Preview of ${name || filename}`}
          src={objectUrl}
          style={{ width: '100%', height: '70vh', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', background: 'var(--color-card)' }}
        />
      );
    }
    if (kind === 'image' && objectUrl) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', maxHeight: '70vh', overflow: 'auto' }}>
          <img src={objectUrl} alt={name || filename} style={{ maxWidth: '100%', height: 'auto', borderRadius: 'var(--r-md)' }} />
        </div>
      );
    }
    if (kind === 'text' && textBody != null) {
      return (
        <pre style={{
          margin: 0, maxHeight: '70vh', overflow: 'auto', padding: 'var(--sp-4)',
          background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
          fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{textBody}</pre>
      );
    }
    // Graceful fallback — unpreviewable type (or empty).
    return (
      <div style={{
        padding: 'var(--sp-7)', minHeight: 160, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        background: 'var(--color-card)', border: '1px dashed var(--color-border)',
        color: 'var(--color-muted)', borderRadius: 'var(--r-lg)',
      }}>
        <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>Preview not available — download instead.</p>
      </div>
    );
  })();

  const actions = (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
      <Button variant="primary" size="sm" onClick={() => downloadDocument(id, (m) => toast({ variant: 'error', message: m }), downloadBase)}>
        Download
      </Button>
      <Button variant="secondary" size="sm" onClick={() => window.open(inlineUrl, '_blank', 'noopener,noreferrer')}>
        Open in new tab
      </Button>
      {isModal && (
        <Button variant="ghost" size="sm" onClick={() => onClose!()}>
          Close
        </Button>
      )}
    </div>
  );

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name || filename}
          </div>
          {filename && name !== filename && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{filename}</div>
          )}
        </div>
        {actions}
      </div>
      {preview}
    </div>
  );

  if (!isModal) {
    // Embedded inline preview (staff detail page).
    return <div data-testid="document-viewer">{body}</div>;
  }

  // Modal overlay (portal). Backdrop click closes.
  return (
    // Backdrop click closes; Escape is the keyboard equivalent (handled above).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      data-testid="document-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Document: ${name || filename}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose!(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 'var(--sp-5, 24px)', overflow: 'auto',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface-raised, var(--color-card, #fff))', color: 'var(--color-ink, #1a1a1a)',
          borderRadius: 'var(--r-lg, 12px)', border: '1px solid var(--color-border, #e2e2df)',
          boxShadow: 'var(--sh-modal, 0 12px 40px rgba(0,0,0,0.3))',
          width: 'min(960px, 100%)', padding: 'var(--sp-5, 24px)',
        }}
      >
        {body}
      </div>
    </div>
  );
}
